import { FC, useCallback, useMemo } from 'react'
import { Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { PasswordElement, useForm } from 'react-hook-form-mui'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'

import Title from './Title'
import useIsNavigatingAway from '../hooks/useIsNavigatingAway'
import useHiddenFormElement from '../hooks/useHiddenFormElement'
import usePageErrorMessages from '../hooks/usePageErrorMessages'
import useFormAnalytics from '../hooks/useFormAnalytics'
import FormContainer from './FormContainer'

interface Fields {
  password: string
}

const ResetPasswordForm: FC = () => {
  const resetPasswordSchema = z.object({
    password: z.string().regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\S]{8,16}$/, 'Invalid password'),
  })

  const formContext = useForm<Fields>({
    resolver: zodResolver(resetPasswordSchema),
  })
  const {
    formState: { errors },
  } = formContext

  const analytics = useFormAnalytics('password_reset')

  const isNavigatingAway = useIsNavigatingAway()
  const simpleModalPlaceholder = useHiddenFormElement('#simplemodal-placeholder')
  const simpleModalOverlay = useHiddenFormElement('#simplemodal-overlay')
  const simpleModalContainer = useHiddenFormElement('#simplemodal-container')
  const isLoading = useMemo(
    () =>
      isNavigatingAway ||
      simpleModalPlaceholder !== null ||
      simpleModalOverlay !== null ||
      simpleModalContainer !== null,
    [isNavigatingAway, simpleModalPlaceholder, simpleModalOverlay, simpleModalContainer],
  )

  const onSubmit = useCallback(
    ({ password }: Fields) => {
      document.querySelector<HTMLInputElement>('#resetPassword')!.value = password
      analytics.onSubmit()
      document.querySelector<HTMLButtonElement>('#continue')!.click()
    },
    [analytics],
  )

  const pageErrorMessages = usePageErrorMessages()

  return (
    <FormContainer formContext={formContext} onSuccess={onSubmit} onError={analytics.onError} onBlur={analytics.onBlur}>
      <Title mb={6}>Reset Password</Title>
      <PasswordElement
        autoFocus
        name="password"
        label="Password"
        autoComplete="new-password"
        required
        disabled={isLoading}
        fullWidth
        parseError={() => ''}
      />
      <Typography variant="caption" mt={1} mb={2} color={errors.password ? 'error' : undefined}>
        8-16 characters, with at least 1 uppercase letter, 1 number &amp; 1 symbol
      </Typography>
      {pageErrorMessages && (
        <Typography component="div" my={2} color="error" dangerouslySetInnerHTML={{ __html: pageErrorMessages }} />
      )}
      <LoadingButton fullWidth sx={{ mt: 6 }} type="submit" variant="contained" loading={isLoading}>
        Set New Password
      </LoadingButton>
    </FormContainer>
  )
}

export default ResetPasswordForm
